import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mvmResultMutedStyle, mvmResultSummaryTextStyle, resourceIconForKey } from './mvmBattleResultUi'
import { MVM_RESULT_MODAL_OVERLAY_Z, MvMResultModalShell } from './mvmResultModalLayout'
import GameButton from '../GameButton'

export interface CastleConquerOffer {
  offerId: string
  allianceName?: string
}

interface MvMVictoryModalProps {
  characterXp?: number
  userXp?: number
  /** Only attacking side sees seized loot UI after an attack win */
  showResourceLoot: boolean
  seizedResources: Record<string, number> | null
  /** Role-specific summary from mvm:battle-ended (server) */
  serverSummary?: string | null
  /**
   * When set, replaces the default / `serverSummary` victory copy (still shown above battle rewards).
   * Use for world-map auto battles (barbarian / tower) that are not hex `mvm:battle-ended` payloads.
   */
  descriptionContent?: React.ReactNode
  /** When false, hides the Character XP / Account XP row (e.g. auto-resolve battles with no XP payload). */
  showBattleRewards?: boolean
  /** Overrides the dismiss CTA label (defaults to `mvm.victory.returnToMap`). */
  dismissButtonLabel?: string
  onClose: () => void
  /** Raise when stacking above other fixed UI (e.g. global attack banner). */
  overlayZIndex?: number
  /**
   * Castle siege win only: when set, the resources are hidden behind the Conquer/Raid choice.
   * Conquer & Recruit ONLY recruits the castle — the leader adds it straight away, anyone else asks
   * their leader to accept — and never keeps resources. Raid ONLY plunders (castle stays theirs).
   * The server creates this offer for every non-task castle win, so both buttons always show.
   */
  conquerOffer?: CastleConquerOffer | null
  /**
   * True for an attacker CASTLE win: the loot is withheld while we wait (briefly) for the conquer
   * offer to arrive. If it arrives → the Conquer/Raid choice shows; if none arrives (server hiccup)
   * the loot is revealed after a short grace period (default raid).
   */
  awaitConquerOffer?: boolean
  /**
   * Returns the server result so the modal can tell "request sent to leader" from "leader recruited
   * it immediately" (`autoApproved`) and from "recruiting was impossible" (`recruitFailed` — e.g. the
   * alliance is full: nothing happens at all, no loot, XP only).
   */
  onConquer?: () => void | Promise<{ autoApproved?: boolean; recruitFailed?: boolean; message?: string } | void>
  onRaid?: () => void
}

export const MvMVictoryModal: React.FC<MvMVictoryModalProps> = ({
  characterXp = 50,
  userXp = 50,
  showResourceLoot,
  seizedResources,
  serverSummary,
  descriptionContent,
  showBattleRewards = true,
  dismissButtonLabel,
  onClose,
  overlayZIndex = MVM_RESULT_MODAL_OVERLAY_Z,
  conquerOffer,
  awaitConquerOffer = false,
  onConquer,
  onRaid,
}) => {
  const { t } = useTranslation()

  // undecided → show the Conquer/Raid choice (loot hidden); raid → reveal loot; conquer → request
  // sent to leader; conquered → leader IS the attacker, so it joined immediately (no request);
  // failed → recruiting was impossible (alliance full / none), so nothing happened but the XP.
  const [decision, setDecision] = useState<'undecided' | 'raid' | 'conquer' | 'conquered' | 'failed'>(
    conquerOffer || awaitConquerOffer ? 'undecided' : 'raid',
  )
  const [failureMessage, setFailureMessage] = useState<string | null>(null)
  /** Once the player has chosen, a late-arriving offer (REST poll losing to the socket) must not reopen the choice. */
  const decidedRef = useRef(false)
  useEffect(() => {
    if (decidedRef.current) return
    setDecision(conquerOffer ? 'undecided' : awaitConquerOffer ? 'undecided' : 'raid')
  }, [conquerOffer?.offerId, awaitConquerOffer])

  // Castle win with no offer yet: withhold the loot briefly; if no offer arrives (e.g. no alliance)
  // reveal it as a default raid after the grace period.
  const [graceElapsed, setGraceElapsed] = useState(!awaitConquerOffer)
  useEffect(() => {
    if (!awaitConquerOffer) {
      setGraceElapsed(true)
      return
    }
    setGraceElapsed(false)
    const id = window.setTimeout(() => setGraceElapsed(true), 3500)
    return () => window.clearTimeout(id)
  }, [awaitConquerOffer])

  const choosing = !!conquerOffer && decision === 'undecided'
  const waitingForOffer = awaitConquerOffer && !conquerOffer && !graceElapsed && decision === 'undecided'

  const loot =
    showResourceLoot && seizedResources && Object.keys(seizedResources).length > 0
      ? Object.entries(seizedResources).filter(([, v]) => (v as number) > 0)
      : []

  const summaryLines = serverSummary?.trim()
    ? serverSummary
        .trim()
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 3)
    : null

  const lootRows = loot.map(([key, value]) => ({
    iconUrl: resourceIconForKey(key),
    name: t(`common.resources.${key}`, { defaultValue: key }),
    amount: value as number,
    sign: '+' as const,
  }))

  const handleRaid = () => {
    decidedRef.current = true
    setDecision('raid')
    onRaid?.()
  }
  const handleConquer = async () => {
    decidedRef.current = true
    setDecision('conquer')
    // If the winner IS the leader, the server joins the castle immediately (no request) → show that.
    // If recruiting is impossible (alliance full, no alliance) nothing happens at all — no loot.
    const res = await onConquer?.()
    if (res && typeof res === 'object') {
      if (res.recruitFailed) {
        setFailureMessage(res.message || null)
        setDecision('failed')
      } else if (res.autoApproved) {
        setDecision('conquered')
      }
    }
  }

  const description = (
    <div>
      {waitingForOffer ? (
        <>
          <p style={mvmResultSummaryTextStyle}>{t('mvm.victory.title', { defaultValue: 'Victory!' })}</p>
          <p style={{ ...mvmResultMutedStyle, marginTop: 'clamp(0.4dvh, 0.6dvh, 6px)' }}>
            {t('mvm.victory.securingCastle', { defaultValue: 'You took the castle — deciding the spoils…' })}
          </p>
        </>
      ) : choosing ? (
        <>
          <p style={mvmResultSummaryTextStyle}>
            {t('mvm.victory.conquerOrRaid', { defaultValue: 'Claim the castle — how?' })}
          </p>
          <div className="mt-[clamp(0.7dvh,1.1dvh,12px)] flex flex-col items-stretch justify-center gap-[clamp(0.6dvh,1dvh,12px)] sm:flex-row sm:items-stretch">
            <div className="flex flex-1 flex-col items-center gap-1.5">
              <GameButton onClick={handleConquer} variant="plate" size="small" className="!w-full sm:!w-[13.5rem]">
                {t('mvm.victory.conquerRecruit', { defaultValue: 'Conquer & Recruit' })}
              </GameButton>
              <span style={{ ...mvmResultMutedStyle, fontSize: '0.68rem', lineHeight: 1.25, textAlign: 'center' }}>
                {t('mvm.victory.conquerHint', { defaultValue: 'Castle joins your alliance · no resources' })}
              </span>
            </div>
            <div className="flex flex-1 flex-col items-center gap-1.5">
              <GameButton onClick={handleRaid} variant="plate" size="small" className="!w-full sm:!w-[13.5rem]">
                {t('mvm.victory.raidForResources', { defaultValue: 'Raid for Resources' })}
              </GameButton>
              <span style={{ ...mvmResultMutedStyle, fontSize: '0.68rem', lineHeight: 1.25, textAlign: 'center' }}>
                {t('mvm.victory.raidHint', { defaultValue: 'Take the spoils · castle stays theirs' })}
              </span>
            </div>
          </div>
        </>
      ) : decision === 'failed' ? (
        <>
          <p style={mvmResultSummaryTextStyle}>
            {t('mvm.victory.conquerFailedTitle', { defaultValue: 'Castle not recruited' })}
          </p>
          <p style={{ ...mvmResultMutedStyle, marginTop: 'clamp(0.4dvh, 0.6dvh, 6px)' }}>
            {failureMessage ||
              t('mvm.victory.conquerFailed', {
                defaultValue:
                  'There was no room to recruit the castle, so nothing was taken — you keep the battle experience.',
              })}
          </p>
        </>
      ) : decision === 'conquered' ? (
        <>
          <p style={mvmResultSummaryTextStyle}>{t('mvm.victory.conquerJoinedTitle', { defaultValue: 'Castle conquered!' })}</p>
          <p style={{ ...mvmResultMutedStyle, marginTop: 'clamp(0.4dvh, 0.6dvh, 6px)' }}>
            {t('mvm.victory.conquerJoined', {
              defaultValue: 'The castle joined {{name}}.',
              name: conquerOffer?.allianceName || t('mvm.victory.yourAlliance', { defaultValue: 'your alliance' }),
            })}
          </p>
        </>
      ) : decision === 'conquer' ? (
        <>
          <p style={mvmResultSummaryTextStyle}>{t('mvm.victory.conquerSentTitle', { defaultValue: 'Conquer requested' })}</p>
          <p style={{ ...mvmResultMutedStyle, marginTop: 'clamp(0.4dvh, 0.6dvh, 6px)' }}>
            {t('mvm.victory.conquerSent', {
              defaultValue: 'Sent to {{name}}’s leader. If approved, the castle joins your alliance.',
              name: conquerOffer?.allianceName || t('mvm.victory.yourAlliance', { defaultValue: 'your alliance' }),
            })}
          </p>
        </>
      ) : descriptionContent ? (
        descriptionContent
      ) : summaryLines && summaryLines.length > 0 ? (
        summaryLines.map((line, i) => (
          <p key={i} style={{ ...mvmResultSummaryTextStyle, marginTop: i ? 'clamp(0.45dvh, 0.65dvh, 6px)' : 0 }}>
            {line}
          </p>
        ))
      ) : (
        <>
          <p style={mvmResultSummaryTextStyle}>{t('mvm.victory.allianceWon')}</p>
          <p style={mvmResultMutedStyle}>
            {t('mvm.victory.rewardSummaryHint', { defaultValue: 'Experience and any spoils are listed below.' })}
          </p>
        </>
      )}
    </div>
  )

  // Loot stays hidden while waiting for the offer, while choosing, and after choosing conquer —
  // including a conquer that could not be recruited, where the resources go back to the defender.
  // A raid (or the grace fallback) reveals it.
  const revealLoot =
    showResourceLoot && !choosing && !waitingForOffer && decision !== 'conquer' && decision !== 'failed'

  return (
    <MvMResultModalShell
      variant="victory"
      overlayZIndex={overlayZIndex}
      logoTitle={t('mvm.victory.title')}
      description={description}
      battleRewardsTitle={t('mvm.resultModal.battleRewards')}
      showBattleRewards={showBattleRewards}
      characterXp={characterXp}
      userXp={userXp}
      characterXpLabel={t('mvm.resultModal.characterXp')}
      userXpLabel={t('mvm.resultModal.accountXp')}
      lootSectionTitle={t('mvm.resultModal.lootWon')}
      showLootSection={revealLoot}
      lootRows={lootRows}
      lootEmptyText={revealLoot && lootRows.length === 0 ? t('mvm.resultModal.noLoot') : null}
      backButtonLabel={dismissButtonLabel ?? t('mvm.victory.returnToMap')}
      onClose={onClose}
    />
  )
}
